(() => {
  if (customElements.get('nps-map')) return;

  const LEAFLET_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet';
  const CLUSTER_URL = 'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js';
  const MAPLIBRE_URL = 'https://unpkg.com/maplibre-gl@5.24.0/dist/maplibre-gl';
  const MAPLIBRE_LEAFLET_URL = 'https://unpkg.com/@maplibre/maplibre-gl-leaflet@0.1.4/leaflet-maplibre-gl.js';
  const FRANCE_BOUNDS = [
    [41.2, -5.6],
    [51.3, 9.8],
  ];
  const PAN_BOUNDS = [
    [39.2, -8.6],
    [58, 12.9],
  ];
  const FOCUS_ZOOM = 11;
  const KIND_ORDER = ['Sommets', 'Lacs', 'Vallées & gorges', 'Parcs naturels', 'Littoral', 'Stations & villages'];
  const scripts = {};

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const loadStylesheet = (href) => {
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.crossOrigin = '';
    document.head.appendChild(link);
  };

  const loadScript = (src) => {
    scripts[src] ||= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.crossOrigin = '';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return scripts[src];
  };

  const loadLeaflet = async (withCluster, withVector) => {
    loadStylesheet(`${LEAFLET_URL}.css`);
    if (!window.L) await loadScript(`${LEAFLET_URL}.js`);
    if (withCluster && !window.L.markerClusterGroup) await loadScript(CLUSTER_URL);
    if (withVector && !window.L.maplibreGL) {
      loadStylesheet(`${MAPLIBRE_URL}.css`);
      if (!window.maplibregl) await loadScript(`${MAPLIBRE_URL}.js`);
      await loadScript(MAPLIBRE_LEAFLET_URL);
    }
    return window.L;
  };

  const createEl = (tag, className, text) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text) el.textContent = text;
    return el;
  };

  class NpsMap extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('.nps-map__canvas');
      this.markers = new Map();
      try {
        this.points = JSON.parse(this.dataset.mapPoints || '[]').filter(
          (point) => Number.isFinite(point.lat) && Number.isFinite(point.lng)
        );
      } catch (error) {
        this.points = [];
      }

      this.setupFilters();

      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.addEventListener('shopify:block:select', this.onBlockSelect);

      if (window.Shopify && Shopify.designMode) {
        this.init();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.init();
        },
        { rootMargin: '300px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      clearTimeout(this.searchTimer);
      clearTimeout(this.restoreTimer);
      this.stopTour();
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.map?.remove();
      this.map = null;
    }

    async init() {
      if (this.map || this.initializing) return;
      this.initializing = true;

      let L;
      try {
        L = await loadLeaflet(this.dataset.cluster === 'true', Boolean(this.dataset.styleUrl));
      } catch (error) {
        this.initializing = false;
        return;
      }
      if (!this.isConnected) return;

      const { dataset } = this;
      const center = [parseFloat(dataset.centerLat) || 46.603354, parseFloat(dataset.centerLng) || 1.888334];
      const maxZoom = parseInt(dataset.maxZoom, 10) || 17;

      const france = L.latLngBounds(FRANCE_BOUNDS);

      this.map = L.map(this.canvas, {
        scrollWheelZoom: false,
        zoomControl: false,
        maxZoom,
        maxBounds: PAN_BOUNDS,
        maxBoundsViscosity: 0.7,
        zoomSnap: 0.25,
      }).setView(center, parseInt(dataset.zoom, 10) || 6);

      const lockMinZoom = () => this.map.setMinZoom(this.map.getBoundsZoom(france));
      lockMinZoom();
      if (this.map.getZoom() > this.map.getMinZoom()) this.map.setZoom(this.map.getMinZoom(), { animate: false });
      this.map.on('resize', lockMinZoom);

      L.control.zoom({ position: 'bottomright' }).addTo(this.map);

      if (dataset.styleUrl) {
        L.maplibreGL({
          style: dataset.styleUrl,
          className: 'nps-map__vector',
          attributionControl: {
            customAttribution:
              '<a href="https://www.maptiler.com/copyright/">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright">&copy; OpenStreetMap contributors</a>',
          },
        }).addTo(this.map);
      } else if (dataset.tileUrl) {
        L.tileLayer(dataset.tileUrl, {
          attribution: dataset.tileAttribution || '',
          maxZoom,
          className: 'nps-map__tiles',
        }).addTo(this.map);
      }

      const icon = L.divIcon({
        className: 'nps-map__marker no-empty',
        iconSize: [14, 14],
        popupAnchor: [0, -8],
      });

      this.layer = L.markerClusterGroup
        ? L.markerClusterGroup({
            maxClusterRadius: parseInt(dataset.clusterRadius, 10) || 15,
            showCoverageOnHover: false,
            spiderfyOnMaxZoom: true,
            iconCreateFunction: (cluster) => this.clusterIcon(L, cluster.getChildCount()),
          })
        : L.layerGroup();
      this.layer.addTo(this.map);

      const canHover = window.matchMedia('(hover: hover)').matches;

      this.points.forEach((point) => {
        const label = [point.place, point.department].filter(Boolean).join(', ');
        const marker = L.marker([point.lat, point.lng], { icon, riseOnHover: true }).bindPopup(() => this.buildPopup(point), {
            className: 'nps-map__popup',
            minWidth: 220,
            maxWidth: point.products.length > 1 ? 300 : 260,
            autoPan: false,
          });

        marker.on('add', () => marker.getElement()?.setAttribute('aria-label', label));
        if (canHover) {
          marker.bindTooltip(this.buildTooltip(point), {
            className: 'nps-map__tooltip',
            direction: 'top',
            offset: [0, -10],
            opacity: 1,
          });
          marker.on('tooltipopen', () => {
            this.stopTour();
            if (marker.isPopupOpen()) marker.closeTooltip();
          });
        }

        marker.on('popupopen', (event) => {
          marker.closeTooltip();
          this.classList.add('has-popup');
          marker.getElement()?.classList.add('is-active');
          this.stopTour();
          this.fitPopup(event.popup);
          this.focusMarker(marker, event.popup);
        });
        marker.on('popupclose', () => {
          marker.getElement()?.classList.remove('is-active');
          this.classList.remove('has-popup');
          this.zoomOutFrom(marker);
        });
        this.markers.set(point.id, marker);
      });
      this.renderMarkers();

      const focus = this.markers.get(dataset.focus);
      if (focus) {
        this.map.setView(focus.getLatLng(), parseFloat(dataset.focusZoom) || 8, { animate: false });
        focus.getElement()?.classList.add('is-focus');
      }

      if (dataset.fitBounds === 'true' && this.points.length > 1) {
        this.map.fitBounds(
          this.points.map((point) => [point.lat, point.lng]),
          { padding: [48, 48], maxZoom: 9 }
        );
      }

      this.initializing = false;
      if (this.pendingBlockId) {
        this.openPoint(this.pendingBlockId);
      } else if (dataset.tour === 'true' && !focus) {
        this.startTour(L);
      }
    }

    // Shows place after place in a tooltip until the visitor interacts with the map.
    startTour(L) {
      this.tourTip = L.tooltip({
        className: 'nps-map__tooltip nps-map__tooltip--tour',
        direction: 'top',
        offset: [0, -10],
        opacity: 1,
      });
      this.stopTour = this.stopTour.bind(this);
      this.onTourClick = (event) => {
        if (event.target.closest('.nps-map__tooltip--tour')) this.tourMarker?.openPopup();
      };
      ['pointerdown', 'wheel', 'keydown', 'focusin'].forEach((type) =>
        this.addEventListener(type, this.stopTour, { passive: true })
      );
      this.addEventListener('click', this.onTourClick);
      this.tourObserver = new IntersectionObserver(([entry]) => {
        this.tourVisible = entry.isIntersecting;
        if (this.tourVisible && !this.tourTimer) this.tourStep();
      });
      this.tourObserver.observe(this.canvas);
    }

    tourStep() {
      this.tourTimer = null;
      if (!this.tourTip || !this.tourVisible || document.hidden) return;
      const area = this.map.getBounds().pad(-0.15);
      const candidates = this.visiblePoints
        .map((point) => ({ point, marker: this.markers.get(point.id) }))
        .filter(({ point, marker }) => point.products?.length && marker?.getElement() && area.contains(marker.getLatLng()))
        .filter(({ marker }) => marker !== this.tourMarker || this.visiblePoints.length < 2);
      const next = candidates[Math.floor(Math.random() * candidates.length)];
      if (!next) return;
      this.tourMarker?.getElement()?.classList.remove('is-active');
      this.tourMarker = next.marker;
      this.tourMarker.getElement().classList.add('is-active');
      this.tourTip.setContent(this.buildTooltip(next.point)).setLatLng(next.marker.getLatLng()).openOn(this.map);
      this.tourTimer = setTimeout(() => {
        this.tourTip.getElement()?.classList.add('is-leaving');
        this.tourTimer = setTimeout(() => {
          this.tourTip.close();
          this.tourTip.getElement()?.classList.remove('is-leaving');
          this.tourMarker?.getElement()?.classList.remove('is-active');
          this.tourTimer = setTimeout(() => this.tourStep(), 400);
        }, 250);
      }, 3200);
    }

    stopTour(event) {
      if (!this.tourTip || event?.target?.closest?.('.nps-map__tooltip--tour')) return;
      clearTimeout(this.tourTimer);
      this.tourObserver.disconnect();
      ['pointerdown', 'wheel', 'keydown', 'focusin'].forEach((type) => this.removeEventListener(type, this.stopTour));
      this.removeEventListener('click', this.onTourClick);
      this.tourTip.close();
      this.tourMarker?.getElement()?.classList.remove('is-active');
      this.tourTip = null;
      this.tourMarker = null;
    }

    setupFilters() {
      this.filters = this.querySelector('.nps-map__filters');
      this.visiblePoints = this.points;
      if (!this.filters || this.points.length < 2) return;

      this.searchInput = this.filters.querySelector('.nps-map__search-input');
      this.departmentSelect = this.filters.querySelector('.nps-map__department-select');
      this.kindSelect = this.filters.querySelector('.nps-map__kind-select');
      this.status = this.filters.querySelector('.nps-map__status');
      this.count = this.filters.querySelector('.nps-map__count');
      this.toggle = this.filters.querySelector('.nps-map__filters-toggle');
      this.badge = this.filters.querySelector('.nps-map__filters-badge');

      this.points.forEach((point) => {
        point.search = normalize(
          [point.place, point.department, point.kind, point.text, ...(point.products || []).map((p) => p.title)].join(' ')
        );
      });

      const departments = [...new Set(this.points.map((point) => point.department).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'fr')
      );
      this.fillSelect(this.departmentSelect, departments);

      const rank = (kind) => (KIND_ORDER.includes(kind) ? KIND_ORDER.indexOf(kind) : KIND_ORDER.length);
      const kinds = [...new Set(this.points.map((point) => point.kind).filter(Boolean))].sort(
        (a, b) => rank(a) - rank(b) || a.localeCompare(b, 'fr')
      );
      this.fillSelect(this.kindSelect, kinds);

      this.searchInput.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.applyFilters(), 200);
      });
      this.searchInput.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        clearTimeout(this.searchTimer);
        this.applyFilters();
        if (this.visiblePoints.length !== 1) return;
        if (this.classList.contains('is-compact')) this.setFiltersOpen(false);
        this.openPoint(this.visiblePoints[0].id);
      });
      this.departmentSelect.addEventListener('change', () => this.applyFilters());
      this.kindSelect.addEventListener('change', () => this.applyFilters());
      this.filters.querySelector('.nps-map__reset').addEventListener('click', () => {
        this.searchInput.value = '';
        this.departmentSelect.value = '';
        this.kindSelect.value = '';
        this.applyFilters();
      });

      this.toggle.addEventListener('click', () => this.setFiltersOpen(this.toggle.getAttribute('aria-expanded') !== 'true'));
      this.resizeObserver = new ResizeObserver(([entry]) => {
        const { width, height } = entry.contentRect;
        const compact = this.dataset.compactFilters === 'true' || width < 480;
        if (compact === this.classList.contains('is-compact')) return;
        this.classList.toggle('is-compact', compact);
        this.setFiltersOpen(!compact);
      });
      this.resizeObserver.observe(this.canvas);

      this.filters.hidden = false;
    }

    setFiltersOpen(open) {
      this.toggle.setAttribute('aria-expanded', String(open));
      this.filters.classList.toggle('is-open', open);
    }

    fillSelect(select, values) {
      if (values.length < 2) {
        select.closest('.nps-map__field').hidden = true;
        return;
      }
      values.forEach((value) => select.add(new Option(value, value)));
    }

    applyFilters() {
      const query = normalize(this.searchInput.value);
      const terms = query ? query.split(/\s+/) : [];
      const department = this.departmentSelect.value;
      const kind = this.kindSelect.value;
      const filtered = Boolean(terms.length || department || kind);

      this.visiblePoints = this.points.filter(
        (point) =>
          (!department || point.department === department) &&
          (!kind || point.kind === kind) &&
          terms.every((term) => point.search.includes(term))
      );

      const total = this.visiblePoints.length;
      this.count.textContent = total ? `${total} lieu${total > 1 ? 'x' : ''} sur ${this.points.length}` : 'Aucun lieu trouvé';
      this.status.hidden = !filtered;
      const active = [terms.length, department, kind].filter(Boolean).length;
      this.badge.textContent = active;
      this.badge.hidden = !active;
      if (!this.map) return;

      this.skipZoomOut = this.classList.contains('has-popup');
      this.map.closePopup();
      this.renderMarkers();
      if (filtered && total) {
        this.map.fitBounds(
          this.visiblePoints.map((point) => [point.lat, point.lng]),
          { ...this.fitPadding(), maxZoom: 9 }
        );
      } else if (!filtered) {
        this.map.fitBounds(FRANCE_BOUNDS);
      }
    }

    fitPadding() {
      const top = this.filters && !this.filters.hidden ? this.filters.offsetHeight + 24 : 48;
      return { paddingTopLeft: [48, top], paddingBottomRight: [48, 48] };
    }

    renderMarkers() {
      this.layer.clearLayers();
      const markers = this.visiblePoints.map((point) => this.markers.get(point.id)).filter(Boolean);
      if (this.layer.addLayers) {
        this.layer.addLayers(markers);
      } else {
        markers.forEach((marker) => this.layer.addLayer(marker));
      }
    }

    moveTo(center, zoom) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.map.setView(center, zoom);
      } else {
        this.map.flyTo(center, zoom, { duration: 0.6 });
      }
    }

    filtersBottom() {
      const shown = this.filters && !this.filters.hidden && !this.classList.contains('is-compact');
      return shown ? this.filters.offsetTop + this.filters.offsetHeight : 0;
    }

    focusMarker(marker, popup) {
      clearTimeout(this.restoreTimer);
      const zoom = Math.min(Math.max(this.map.getZoom(), FOCUS_ZOOM), this.map.getMaxZoom());
      const offset = ((popup.getElement()?.offsetHeight || 0) + 20 + this.filtersBottom()) / 2;
      const center = this.map.unproject(this.map.project(marker.getLatLng(), zoom).subtract([0, offset]), zoom);
      this.moveTo(center, zoom);
    }

    fitPopup(popup) {
      const element = popup.getElement();
      const list = element?.querySelector('.nps-map__products');
      if (!list) return;
      list.style.maxHeight = '';
      const overflow = element.offsetHeight + 40 - this.canvas.offsetHeight;
      if (overflow > 0) list.style.maxHeight = `${Math.max(list.offsetHeight - overflow, 76)}px`;
    }

    zoomOutFrom(marker) {
      clearTimeout(this.restoreTimer);
      if (this.skipZoomOut) {
        this.skipZoomOut = false;
        return;
      }
      // Deferred so that switching markers (close then open) does not zoom out.
      this.restoreTimer = setTimeout(() => {
        if (!this.map) return;
        this.moveTo(marker.getLatLng(), Math.max(this.map.getZoom() - 1, this.map.getMinZoom()));
      }, 50);
    }

    clusterIcon(L, count) {
      const size = count < 10 ? 40 : 48;
      return L.divIcon({
        className: 'nps-map__cluster',
        html: `<span class="nps-map__cluster-count">${count}</span><span class="visually-hidden">${this.dataset.clusterLabel || ''}</span>`,
        iconSize: [size, size],
      });
    }

    buildTooltip(point) {
      const tip = createEl('div', 'nps-map__tip');
      const image = (point.products || []).find((product) => product.image);
      if (image) {
        const media = createEl('span', 'nps-map__tip-media');
        media.appendChild(this.buildImage({ ...image, imageAlt: '' }, 48));
        tip.appendChild(media);
      }
      const info = createEl('span', 'nps-map__tip-info');
      if (point.department) info.appendChild(createEl('span', 'nps-map__tip-department', point.department));
      if (point.place) info.appendChild(createEl('span', 'nps-map__tip-place', point.place));
      tip.appendChild(info);
      return tip;
    }

    buildPopup(point) {
      const products = point.products || [];
      // A full-size product photo does not fit in a short map.
      const compact = this.canvas.offsetHeight < 480;
      this.classList.toggle('is-short', compact);
      const single = products.length === 1 && !compact ? products[0] : null;
      const card = createEl('article', `nps-map__card${single ? '' : ' nps-map__card--multi'}${compact ? ' nps-map__card--compact' : ''}`);

      if (single?.image) {
        const media = createEl('div', 'nps-map__card-media');
        media.appendChild(this.buildImage(single, 520));
        card.appendChild(media);
      }

      const body = createEl('div', 'nps-map__card-body');
      if (point.department) body.appendChild(createEl('p', 'nps-map__card-department', point.department));
      if (point.place) body.appendChild(createEl('h3', 'nps-map__card-place', point.place));
      if (single) body.appendChild(createEl('p', 'nps-map__card-product', single.title));
      if (point.text) body.appendChild(createEl('p', 'nps-map__card-text', point.text));

      if (single) {
        if (single.price) body.appendChild(this.buildPrice(single, 'nps-map__card-price'));
        const cta = createEl('a', 'nps-map__card-cta', this.dataset.ctaLabel || single.title);
        cta.href = single.url;
        body.appendChild(cta);
      } else if (products.length) {
        const list = createEl('ul', 'nps-map__products');
        products.forEach((product) => {
          const item = createEl('li');
          const link = createEl('a', 'nps-map__product');
          link.href = product.url;
          const thumb = createEl('span', 'nps-map__product-media');
          if (product.image) thumb.appendChild(this.buildImage(product, 120));
          const info = createEl('span', 'nps-map__product-info');
          info.appendChild(createEl('span', 'nps-map__product-title', product.title));
          if (product.price) info.appendChild(this.buildPrice(product, 'nps-map__product-price', 'span'));
          link.append(thumb, info);
          item.appendChild(link);
          list.appendChild(item);
        });
        body.appendChild(list);
      }

      card.appendChild(body);
      return card;
    }

    buildImage(product, size) {
      const img = createEl('img');
      img.src = size < 520 ? product.image.replace(/width=\d+/, `width=${size * 2}`) : product.image;
      img.alt = product.imageAlt || '';
      img.loading = 'lazy';
      img.width = size;
      img.height = size;
      return img;
    }

    buildPrice(product, className, tag = 'p') {
      const price = createEl(tag, className);
      price.innerHTML = product.price;
      return price;
    }

    openPoint(blockId) {
      const marker = this.markers.get(blockId);
      if (!marker || !this.layer.hasLayer(marker)) return;
      this.pendingBlockId = null;
      if (this.layer.zoomToShowLayer) {
        this.layer.zoomToShowLayer(marker, () => marker.openPopup());
      } else {
        this.map.panTo(marker.getLatLng());
        marker.openPopup();
      }
    }

    onBlockSelect(event) {
      const blockId = event.target.dataset.blockId;
      if (!blockId) return;
      if (this.map) {
        this.openPoint(blockId);
      } else {
        this.pendingBlockId = blockId;
        this.init();
      }
    }
  }

  customElements.define('nps-map', NpsMap);
})();
